import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Container,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { DailyControlUser } from '../../../api/daily_control/models/UserModels';
import { ApiDailyControlUser } from '../../../api/daily_control/ApiDailyControlUser';

const DailyControlUserList = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DailyControlUser[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchFullText, setSearchFullText] = useState<string>('');
  const [toggleValues, setToggleValues] = useState(() => ['']);
  const [debounceSearchFullText] = useDebounce(searchFullText, 500);
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  const columnLowCredits = useCallback((user: DailyControlUser): string[] => {
    const lowCredits: string[] = [];

    const remaining_fluid_credit = user.remaining_fluid_credit;
    const remaining_solid_credit = user.remaining_solid_credit;
    if (
      !user.customer_on_demand &&
      remaining_fluid_credit !== null &&
      user.fluid_credit_limit !== null &&
      remaining_fluid_credit < 0.2 * user.fluid_credit_limit
    ) {
      lowCredits.push('remaining_fluid_credit');
    }
    if (
      remaining_solid_credit !== null &&
      user.solid_credit_limit !== null &&
      remaining_solid_credit < 0.2 * user.solid_credit_limit
    ) {
      lowCredits.push('remaining_solid_credit');
    }
    return lowCredits;
  }, []);

  const renderCreditsCell = useCallback(
    (limitColumn: number | null, user: DailyControlUser, value: number) => {
      if (limitColumn === null) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {value}
            <Tooltip title={t('daily_control_users.column_info')}>
              <InfoIcon
                sx={{
                  width: '0.8rem',
                  height: '0.8rem',
                  position: 'relative',
                  top: '-6px',
                }}
                fontSize="small"
                color="info"
              />
            </Tooltip>
          </Box>
        );
      } else {
        const lowCredits = columnLowCredits(user);
        if (
          lowCredits.length > 0 &&
          lowCredits.includes('remaining_solid_credit')
        ) {
          return <b>{value}</b>;
        }
        return value;
      }
    },
    [t, columnLowCredits]
  );

  let columns = useMemo<GridColDef<DailyControlUser>[]>(
    () => [
      { field: 'id', headerName: 'ID', flex: 1, maxWidth: 100 },
      {
        field: 'customer_id',
        headerName: 'Customer ID',
        flex: 1,
        minWidth: 100,
      },
      { field: 'username', headerName: 'Userame', flex: 1, minWidth: 100 },
      { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
      { field: 'role', headerName: 'Role', flex: 1, minWidth: 80 },
      {
        field: 'department_id',
        headerName: 'Department ID',
        align: 'center',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'customer_on_demand',
        headerName: 'Customer On Demand',
        flex: 1,
        type: 'boolean',
        minWidth: 180,
      },
      {
        field: 'remaining_fluid_credit',
        headerName: 'A Credits',
        sortable: false,
        flex: 1,
        minWidth: 90,
        renderCell: (params: any) =>
          renderCreditsCell(
            params.row.fluid_credit_limit,
            params.row,
            params.row.remaining_fluid_credit
          ),
      },
      {
        field: 'remaining_solid_credit',
        headerName: 'B Credits',
        sortable: false,
        flex: 1,
        minWidth: 90,
        renderCell: (params: any) =>
          renderCreditsCell(
            params.row.solid_credit_limit,
            params.row,
            params.row.remaining_solid_credit
          ),
      },
      {
        field: 'fluid_credit_limit',
        headerName: 'A Credit Limit',
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'expiring_in',
        headerName: 'Contract Status',
        flex: 1,
        minWidth: 150,
        renderCell: (params: any) => {
          if (params.row.expiring_in) {
            const value = params.row.expiring_in;
            const expiring = `Expiring in ${value} days`;
            if (value >= 0 && value <= 7) {
              return <b>{expiring}</b>;
            } else if (value > 7) {
              return expiring;
            } else {
              return `Expired ${Math.abs(value)} days ago`;
            }
          } else {
            return '';
          }
        },
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        flex: 1,
        minWidth: 100,
        valueGetter: (value, row) => new Date(row.start_date),
        renderCell: (params: any) =>
          new Date(params.row.start_date).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 1,
        minWidth: 100,
        valueGetter: (value, row) =>
          row.end_date ? new Date(row.end_date) : '',
        renderCell: (params: any) =>
          params.row.end_date
            ? new Date(params.row.end_date).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '',
      },
      {
        field: 'contract_type',
        headerName: 'Contract Type',
        flex: 1,
        align: 'center',
        minWidth: 110,
      },
      {
        field: 'renewal_type',
        headerName: 'Renewal Type',
        flex: 1,
        align: 'center',
        minWidth: 110,
      },
      {
        field: 'api_docs_visible',
        headerName: 'Api Docs',
        flex: 1,
        type: 'boolean',
        minWidth: 80,
      },
      {
        field: 'dop_doc_visible',
        headerName: 'DOP Docs',
        flex: 1,
        type: 'boolean',
        minWidth: 90,
      },
      {
        field: 'screening',
        headerName: 'Screening',
        flex: 1,
        type: 'boolean',
        minWidth: 80,
      },
      {
        field: 'monitoring',
        headerName: 'Monitoring',
        flex: 1,
        type: 'boolean',
        minWidth: 90,
      },
      {
        field: 'batch_screening',
        headerName: 'Batch Screening',
        flex: 1,
        type: 'boolean',
        minWidth: 120,
      },
      {
        field: 'control_center',
        headerName: 'Control Center',
        flex: 1,
        type: 'boolean',
        minWidth: 110,
      },
    ],
    [renderCreditsCell]
  );

  /**
   * Fetch the paginated users from the API and set the rows state
   */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { total, users } = await ApiDailyControlUser.index({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        search: debounceSearchFullText,
        expiring: toggleValues.some((value) => value.includes('expiring')),
        low_credits: toggleValues.some((value) =>
          value.includes('low_credits')
        ),
        sort_field: sortModel[0]?.field ?? 'id',
        sort_direction: sortModel[0]?.sort ?? 'asc',
      });
      setRowCount(total);
      setRows(users);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the users: 
        ${error.response?.data.error ?? 'Error fetching the users'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debounceSearchFullText,
    toggleValues,
    sortModel,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const handleToggleValueChange = (newFormats: string[]) => {
    setToggleValues(newFormats);
  };

  const getClassName = (user: DailyControlUser): string => {
    if (user.expiring_in < 0) {
      return 'expired-contract';
    } else if (
      (user.expiring_in && user.expiring_in >= 0 && user.expiring_in <= 7) ||
      columnLowCredits(user).length > 0
    ) {
      return 'expiring-contract';
    }
    return '';
  };

  return (
    <Container>
      <Card sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
        <Stack
          spacing={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchFullText}
              sx={{
                backgroundColor: 'white',
                width: { xs: '100%', sm: 'calc(100% - 200px)' },
              }}
              onChange={(e) => setSearchFullText(e.target.value)}
            />
            <ToggleButtonGroup
              size="small"
              sx={{ flexWrap: 'wrap' }}
              value={toggleValues}
              onChange={(e, value) => handleToggleValueChange(value)}
            >
              <ToggleButton value="expiring">
                {t('hexa_api.filters.expiring')}
              </ToggleButton>
              <ToggleButton value="low_credits">
                {t('hexa_api.filters.low_credits')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <div style={{ display: 'block', overflow: 'auto' }}>
            <DataGridPremium
              getRowId={(row) => row.id}
              getRowClassName={(params) => getClassName(params.row)}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              loading={loading}
              disableColumnResize={false}
              scrollbarSize={20}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={rowCount}
              paginationMode="server"
              pagination={true}
              pageSizeOptions={[10, 25, 50, 100]}
              autoHeight={true}
              rows={rows}
              columns={columns}
            />
          </div>
        </Stack>
        <Snackbar
          open={error.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error">
            {error.message}
          </Alert>
        </Snackbar>
      </Card>
    </Container>
  );
};

export default DailyControlUserList;
